/**
 * Utilities for traversing JSON node trees
 */

import {hasFlattenedOverrides} from '@packages/systems/core/render';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {getSymbolRootIdFromInstance} from './JsonNodeComponentUtils';

type JSONNodeVisitor = (jsonNode: JSONNode) => void;
type JSONNodeIdVisitor = (jsonNodeId: string) => void;

/**
 * Performs a depth-first search of a JSON node tree starting at `node`
 * and calls the `visit` function for each JSON node (including `node`).
 *
 * -- WARNING --
 * This function assumes the JSON node tree is acyclic and will throw a
 * RangeError if given a tree with a cycle. Use this utility only with
 * JSON node trees that are guaranteed to be acyclic.
 *
 * For example, JSON nodes that have been patched using the site data
 * migrations system are guaranteed to be acyclic.
 */
export function forEachTree(
  node: JSONNode,
  nodeIndex: {
    [nodeId: string]: JSONNode;
  },
  visit: JSONNodeVisitor
): void {
  visit(node);
  forEachChild(node, function visitChildNode(childId: string) {
    const childNode = nodeIndex[childId];
    if (childNode) {
      forEachTree(childNode, nodeIndex, visit);
    }
  });
}

/**
 * Finds every direct descendant of a JSON node, from the children prop
 * and from component properties in JSON node data, and calls a `visit`
 * function with the expected JSON node ID of each direct descendant.
 */
export function forEachChild(node: JSONNode, visit: JSONNodeIdVisitor): void {
  forEachComponentPropertyChild(node, visit);

  // visit JSON node's direct descendants found in children prop
  if (Array.isArray(node.children)) {
    for (const childId of node.children) {
      visit(childId);
    }
  }
}

/**
 * Finds every direct descendant of a JSON node from component
 * properties in JSON node data, and calls a `visit` function with the
 * expected JSON node ID of each component property direct descendant.
 */
export function forEachComponentPropertyChild(
  node: JSONNode,
  visit: JSONNodeIdVisitor
): void {
  // visit JSON node's direct descendants found in component properties
  const overrides = node.data?.sym?.overrides;
  if (overrides != null) {
    for (const overrideKey in overrides) {
      if (hasFlattenedOverrides(overrides[overrideKey])) {
        for (const childId of overrides[overrideKey]) {
          visit(childId);
        }
      }
    }
  }
}

/**
 * Returns id every symbol root instance in an array of JSON nodes.
 */
export const getSymbolRootIds = (nodes: JSONNode[]): Set<string> => {
  return nodes.reduce((acc, node) => {
    if (node.type === 'Symbol') {
      const symbolRootId = getSymbolRootIdFromInstance(node);
      if (symbolRootId !== undefined) {
        acc.add(symbolRootId);
      }
    }
    return acc;
  }, new Set<string>());
};
